import { useEffect, useState, useMemo } from "react";
import {
    Box,
    Button,
    Chip,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Typography,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    styled,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { useAuth, useMessage } from "../../sdk/hooks";
import { formatCurrency } from "../../sdk/utils/numbers";
import { Paper } from "../../components/common";
import * as scheduleApi from "../../sdk/utils/api/price-change-schedule";
import { IPriceChangeSchedule } from "../../sdk/utils/api/price-change-schedule";

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    marginBottom: theme.spacing(1),
}));

const statusConfig: Record<number, { label: string; color: "warning" | "success" | "default" | "error" }> = {
    0: { label: "Scheduled", color: "warning" },
    1: { label: "Active", color: "success" },
    2: { label: "Completed", color: "default" },
    3: { label: "Cancelled", color: "error" },
};

const formatPaisa = (paisa: number) => formatCurrency(paisa / 100);

const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

interface Props {
    variantId: string;
    currentPrice: number;
}

interface ChartPoint {
    timestamp: number;
    price: number;
    label: string;
}

const DATE_RANGES = [
    { label: "15D", days: 15 },
    { label: "30D", days: 30 },
    { label: "3M", days: 90 },
    { label: "6M", days: 180 },
    { label: "1Y", days: 365 },
    { label: "All", days: 0 },
] as const;

export function PriceScheduleSection({ variantId, currentPrice }: Props) {
    const { token } = useAuth();
    const { showError, showSuccess } = useMessage();
    const [schedules, setSchedules] = useState<IPriceChangeSchedule[]>([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [rangeDays, setRangeDays] = useState<number>(90);

    const fetchSchedules = async () => {
        if (!token || !variantId) return;
        try {
            const data = await scheduleApi.getSchedulesForVariant(token, variantId);
            setSchedules(data);
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSchedules();
    }, [token, variantId]);

    const rangeFilteredSchedules = useMemo(() => {
        if (rangeDays === 0) return schedules;
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - rangeDays);
        return schedules.filter(
            (s) => new Date(s.endsAt) >= cutoff || s.status === 0 || s.status === 1
        );
    }, [schedules, rangeDays]);

    const { chartData, xDomain } = useMemo(() => {
        const nonCancelled = rangeFilteredSchedules.filter((s) => s.status !== 3);

        const now = Date.now();
        let windowStart: number;
        let windowEnd: number;

        if (rangeDays === 0 && nonCancelled.length > 0) {
            const allTimes = nonCancelled.flatMap((s) => [
                new Date(s.startsAt).getTime(),
                new Date(s.endsAt).getTime(),
            ]);
            windowStart = Math.min(...allTimes);
            windowEnd = Math.max(...allTimes);
            const pad = (windowEnd - windowStart) * 0.1 || 86400000;
            windowStart -= pad;
            windowEnd += pad;
        } else {
            windowStart = now - rangeDays * 86400000;
            const futureEnds = nonCancelled
                .map((s) => new Date(s.endsAt).getTime())
                .filter((t) => t > now);
            windowEnd = futureEnds.length > 0
                ? Math.max(...futureEnds) + 86400000
                : now + 7 * 86400000;
        }

        if (nonCancelled.length === 0) {
            return {
                chartData: [
                    { timestamp: windowStart, price: currentPrice, label: "Current price" },
                    { timestamp: windowEnd, price: currentPrice, label: "Current price" },
                ],
                xDomain: [windowStart, windowEnd] as [number, number],
            };
        }

        const points: ChartPoint[] = [];
        const sorted = [...nonCancelled].sort(
            (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
        );

        points.push({
            timestamp: windowStart,
            price: currentPrice,
            label: "Base price",
        });

        for (const schedule of sorted) {
            const start = new Date(schedule.startsAt).getTime();
            const end = new Date(schedule.endsAt).getTime();

            points.push({
                timestamp: start - 1,
                price: schedule.baseSellingPrice,
                label: "Before schedule",
            });
            points.push({
                timestamp: start,
                price: schedule.sellingPrice,
                label: `Schedule starts — ${formatPaisa(schedule.sellingPrice)}`,
            });
            points.push({
                timestamp: end - 1,
                price: schedule.sellingPrice,
                label: `Schedule active — ${formatPaisa(schedule.sellingPrice)}`,
            });
            points.push({
                timestamp: end,
                price: schedule.baseSellingPrice,
                label: "Schedule ends — reverts",
            });
        }

        points.push({
            timestamp: windowEnd,
            price: currentPrice,
            label: "Current price",
        });

        points.sort((a, b) => a.timestamp - b.timestamp);
        return {
            chartData: points,
            xDomain: [windowStart, windowEnd] as [number, number],
        };
    }, [rangeFilteredSchedules, currentPrice, rangeDays]);

    const activeSchedule = rangeFilteredSchedules.find((s) => s.status === 1);
    const upcomingSchedules = rangeFilteredSchedules.filter((s) => s.status === 0);
    const pastSchedules = rangeFilteredSchedules.filter((s) => s.status === 2 || s.status === 3);

    if (loading) return null;

    return (
        <Paper>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <SectionTitle sx={{ mb: 0 }}>Price Schedules</SectionTitle>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateOpen(true)}
                >
                    Schedule
                </Button>
            </Box>

            <Box display="flex" gap={0.5} mb={2} flexWrap="wrap">
                {DATE_RANGES.map((range) => (
                    <Chip
                        key={range.label}
                        label={range.label}
                        size="small"
                        variant={rangeDays === range.days ? "filled" : "outlined"}
                        color={rangeDays === range.days ? "primary" : "default"}
                        onClick={() => setRangeDays(range.days)}
                        sx={{ cursor: "pointer" }}
                    />
                ))}
            </Box>

            {schedules.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                    No price change schedules for this variant
                </Typography>
            )}

            {chartData.length > 0 && schedules.length > 0 && (
                <Box sx={{ width: "100%", height: { xs: 200, sm: 280 }, mb: { xs: 2, sm: 3 }, overflow: "hidden" }}>
                    <ResponsiveContainer>
                        <AreaChart
                            data={chartData}
                            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                            <XAxis
                                dataKey="timestamp"
                                type="number"
                                domain={xDomain}
                                tickFormatter={(ts) =>
                                    new Date(ts).toLocaleDateString("en-IN", {
                                        day: "2-digit",
                                        month: "short",
                                    })
                                }
                                scale="time"
                                fontSize={12}
                            />
                            <YAxis
                                tickFormatter={(v) => `₹${(v / 100).toFixed(0)}`}
                                fontSize={12}
                                width={70}
                            />
                            <Tooltip
                                labelFormatter={(ts) =>
                                    new Date(ts as number).toLocaleString("en-IN")
                                }
                                formatter={(value) => [
                                    formatPaisa(Number(value ?? 0)),
                                    "Price",
                                ]}
                            />
                            <ReferenceLine
                                y={currentPrice}
                                stroke="#999"
                                strokeDasharray="3 3"
                                label={{
                                    value: `Current: ${formatPaisa(currentPrice)}`,
                                    position: "right",
                                    fontSize: 11,
                                    fill: "#999",
                                }}
                            />
                            <Area
                                type="stepAfter"
                                dataKey="price"
                                stroke="#1976d2"
                                fill="#1976d233"
                                strokeWidth={2}
                                dot={false}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            )}

            {activeSchedule && (
                <Box mb={2}>
                    <Typography variant="subtitle2" color="success.main" gutterBottom>
                        Currently Active
                    </Typography>
                    <ScheduleCard schedule={activeSchedule} />
                    <Divider sx={{ mt: 2 }} />
                </Box>
            )}

            {upcomingSchedules.length > 0 && (
                <Box mb={2}>
                    <Typography variant="subtitle2" color="warning.main" gutterBottom>
                        Upcoming ({upcomingSchedules.length})
                    </Typography>
                    <List dense disablePadding>
                        {upcomingSchedules.map((s) => (
                            <ScheduleCard key={s.id} schedule={s} />
                        ))}
                    </List>
                    <Divider sx={{ mt: 2 }} />
                </Box>
            )}

            {pastSchedules.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Past ({pastSchedules.length})
                    </Typography>
                    <List dense disablePadding>
                        {pastSchedules.map((s) => (
                            <ScheduleCard key={s.id} schedule={s} />
                        ))}
                    </List>
                </Box>
            )}

            <CreateScheduleDialog
                open={createOpen}
                variantId={variantId}
                onClose={() => {
                    setCreateOpen(false);
                    fetchSchedules();
                }}
            />
        </Paper>
    );
}

function ScheduleCard({ schedule }: { schedule: IPriceChangeSchedule }) {
    const navigate = useNavigate();
    const status = statusConfig[schedule.status] || { label: "Unknown", color: "default" as const };

    return (
        <ListItemButton
            disableGutters
            sx={{ alignItems: "flex-start", py: 1, borderRadius: 1 }}
            onClick={() => navigate(`/price-schedules/${schedule.id}`)}
        >
            <ListItemText
                primary={
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="body2" fontWeight={600}>
                            {formatPaisa(schedule.sellingPrice)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            (original: {formatPaisa(schedule.baseSellingPrice)})
                        </Typography>
                        <Chip label={status.label} color={status.color} size="small" />
                    </Box>
                }
                secondary={
                    <Typography variant="caption" color="text.secondary">
                        {formatDateTime(schedule.startsAt)} &mdash; {formatDateTime(schedule.endsAt)}
                        {schedule.notes && ` — ${schedule.notes}`}
                    </Typography>
                }
            />
        </ListItemButton>
    );
}

function CreateScheduleDialog({
    open,
    variantId,
    onClose,
}: {
    open: boolean;
    variantId: string;
    onClose: () => void;
}) {
    const { token } = useAuth();
    const { showError, showSuccess } = useMessage();

    const [sellingPrice, setSellingPrice] = useState("");
    const [startsAt, setStartsAt] = useState("");
    const [endsAt, setEndsAt] = useState("");
    const [platform, setPlatform] = useState(0);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const handleSubmit = async () => {
        if (!token) return;
        if (!sellingPrice || !startsAt || !endsAt) {
            showError("Please fill in all required fields");
            return;
        }

        setSaving(true);
        try {
            await scheduleApi.createSchedule(token, variantId, {
                sellingPrice: Math.round(Number(sellingPrice) * 100),
                startsAt: new Date(startsAt).toISOString(),
                endsAt: new Date(endsAt).toISOString(),
                platforms: [platform],
                notes: notes || undefined,
            });
            showSuccess("Price schedule created");
            handleClose();
        } catch (err: any) {
            showError(err.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setSellingPrice("");
        setStartsAt("");
        setEndsAt("");
        setPlatform(0);
        setNotes("");
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Schedule Price Change</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <TextField
                        label="Selling Price (₹)"
                        type="number"
                        size="small"
                        value={sellingPrice}
                        onChange={(e) => setSellingPrice(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <Box component="span" sx={{ mr: 0.5, color: "text.secondary" }}>₹</Box>
                            ),
                        }}
                        fullWidth
                        required
                    />
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                        <TextField
                            label="Starts At"
                            type="datetime-local"
                            size="small"
                            value={startsAt}
                            onChange={(e) => setStartsAt(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Ends At"
                            type="datetime-local"
                            size="small"
                            value={endsAt}
                            onChange={(e) => setEndsAt(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            required
                        />
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Platform</InputLabel>
                            <Select
                                value={platform}
                                label="Platform"
                                onChange={(e) => setPlatform(Number(e.target.value))}
                            >
                                <MenuItem value={0}>Website</MenuItem>
                                <MenuItem value={1}>Amazon</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Notes"
                            size="small"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            fullWidth
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                    {saving ? "Creating..." : "Create"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
